import { Accreditation, Brand, Qualification, ServiceFilter } from '@prisma/client'
import { z } from 'zod'

export type MediaFilter = 'videos'

export type Filters = {
  distance: DistanceFilter
  accreditations?: string[]
  qualifications?: string[]
  services?: ServiceFilter[]
  media?: MediaFilter[]
  brands?: string[]
}

export const DEFAULT_FILTERS: Filters = {
  distance: 100,
}

type DistanceFilter = typeof DISTANCE_FILTER_OPTIONS[number]

export const DISTANCE_FILTER_OPTIONS = [20, 50, 100] as const

export function filtersToParams(filters: Filters): URLSearchParams {
  function arrayToParams(key: string, values: string[] | undefined): [string, string][] {
    return values
      ? values.map(v => [key, v])
      : []
  }

  return new URLSearchParams([
    ['distance', String(filters.distance)],
    ...arrayToParams('accreditations', filters.accreditations),
    ...arrayToParams('qualifications', filters.qualifications),
    ...arrayToParams('services', filters.services),
    ...arrayToParams('media', filters.media),
    ...arrayToParams('brands', filters.brands),
  ])
}

export function parseFilterParams(
  params: unknown,
  accreditations: Accreditation[],
  qualifications: Qualification[],
  brands: Brand[],
): Filters {
  return z
    .object({
      distance: distanceFilterSchema,
      accreditations: filterSchema(oneOfSchema(accreditations)),
      qualifications: filterSchema(oneOfSchema(qualifications)),
      services: filterSchema(z.nativeEnum(ServiceFilter)),
      media: filterSchema(z.literal('videos')),
      brands: filterSchema(oneOfSchema(brands)),
    })
    .catch(DEFAULT_FILTERS)
    .parse(params)
}

const intSchema = z.number().int()
  .or(z.string().transform((s, ctx) => {
    const value = parseInt(s)

    if (isNaN(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'not a valid integer',
      })
      return z.NEVER
    } else {
      return value
    }
  })) satisfies z.ZodType<number, any, unknown>

function filterSchema<T>(inner: z.ZodType<T, any, unknown>): z.ZodType<T[] | undefined, any, unknown> {
  return z
    .array(inner)
    .or(inner.transform(e => [e]))
    .optional()
    .catch(undefined)
}

function oneOfSchema(values: { id: string }[]): z.ZodType<string, any, unknown> {
  const [value1, value2, ...rest] = values

  return value1 != null && value2 != null
    ? z.union([
      z.literal(value1.id),
      z.literal(value2.id),
      ...rest.map(value => z.literal(value.id)),
    ])
    : value1 != null
      ? z.literal(value1.id)
      : z.never()
}

export const distanceFilterSchema = intSchema
  .optional()
  .pipe(z.union([
    z.literal(20),
    z.literal(50),
    z.literal(100),
  ]))
  .catch(DEFAULT_FILTERS['distance']) satisfies z.ZodType<DistanceFilter, any, unknown>

export const mediaFilterSchema = z.literal('videos') satisfies z.ZodType<MediaFilter, any, unknown>
